import { ChangeEvent, useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Autocomplete, Box, Button, Card, Checkbox, FormControl, IconButton, MenuItem, Select, TextField } from "@mui/material"
import { useSelector } from "react-redux"
import CompliancePlip from "../../../../components/CompliancePlip"
import debounce from "../../../../helpers/debounce"
import { Biobox, ChildPracticeMetadata, ComplianceStatus, Groups } from "../../../../types"
import BiologicalInventoryTable from "./BiologicalInventoryTable"
import { getBioBoxes } from "../../../../services/customerService"
import { exportBioBoxes } from "../utils/exports"
import InventoryButtons from "../../waterline-management/inventory/InventoryButtons"
import { selectGroups } from "../../../../redux/customer"
import { selectCurrentUser } from "../../../../redux/user"
import { FormattedBioBox, UpdateBioboxPracticePayload } from "../types"
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { updateBoxPractice } from '../services'
import { addError, addSuccess } from "../../../../redux/alerts"
import { useAppDispatch } from "../../../../redux/store"
import useCustomerNumber from '../../../../hooks/useCustomerNumber'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const BiologicalInventory = () => {
  const dispatch = useAppDispatch()

  const customerNumber = useCustomerNumber()
  const currentUser = useSelector(selectCurrentUser)
  const groups = useSelector(selectGroups)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('active')
  const [bioBoxes, setBioBoxes] = useState<FormattedBioBox[] | null>(null)
  const [dropDownFilter, setDropDownFilter] = useState('active')
  const [selectedGroups, setSelectedGroups] = useState<Groups[]>([])
  const [exporting, setExporting] = useState(false)

  const getData = async () => {
    try {
      const bioBoxResponse = await getBioBoxes(customerNumber, currentUser)
      const formattedBioBoxes: Record<number, FormattedBioBox> = {}

      bioBoxResponse.forEach((biobox: Biobox) => {
        if (!formattedBioBoxes[biobox.box_id]) {
          const box: FormattedBioBox = {
            box_id: biobox.box_id,
            display_box_number: biobox.box_type === 'mesa' ? biobox.box_number : `${biobox.lot_number}-${biobox.box_number}`,
            tests_left: biobox.box_size - biobox.tests_run,
            tests_run: biobox.tests_run,
            box_size: biobox.box_size,
            box_number: biobox.box_number,
            lot_number: biobox.lot_number,
            active: biobox.active,
            exp_date: biobox.exp_date.slice(0, 10),
            city: biobox.city,
            customer_number: biobox.customer_number,
            name: biobox.name,
            state: biobox.state,
            compliance: [{
              compliance_code: biobox.compliance_code,
              message_long: biobox.message_long,
              message_short: biobox.message_short,
              compliance_status: biobox.compliance_status,
            }],
          }
          formattedBioBoxes[biobox.box_id] = box
        } else {
          formattedBioBoxes[biobox.box_id].compliance.push({
            compliance_code: biobox.compliance_code,
            message_long: biobox.message_long,
            message_short: biobox.message_short,
            compliance_status: biobox.compliance_status,
          })
        }
      })
      setBioBoxes(Object.values(formattedBioBoxes))
    } catch (error) {
      console.error(error)
      dispatch(addError(error instanceof Error ? error.message : 'An error has occurred while processing your request.'))
    }
  }

  const filterByDropDown = (box: FormattedBioBox) => {
    const status = box.compliance[0]?.compliance_status
    return dropDownFilter === 'active' || Number(status) === Number(dropDownFilter)
  }

  const filterByGroups = (box: FormattedBioBox) => {
    const selectedGroupIds = new Set(selectedGroups.map(g => Number(g.group_id)))
    const memberCustomerNumbers = new Set<number>()
    for (const group of groups) {
      if (selectedGroupIds.has(Number(group.group_id))) {
        for (const member of group.members) {
          memberCustomerNumbers.add(Number(member.customer_number))
        }
      }
    }
    return memberCustomerNumbers.has(Number(box.customer_number))
  }

  const getFilteredBioBoxes = (search: string, statusFilter: string) => {
    const isActive = statusFilter === 'active'
    const groupsSelected = selectedGroups.length

    let filtered: FormattedBioBox[] = bioBoxes ? [...bioBoxes].filter(item => {
      if (isActive) return item.compliance[0].compliance_status !== ComplianceStatus.Inactive
      return item.compliance[0].compliance_status === ComplianceStatus.Inactive
    }) : []

    if (search || groupsSelected || dropDownFilter) {
      filtered = filtered.filter(item => {
        const cleanSearch = search.trim().toLowerCase()

        const practiceName = item.name?.toLowerCase() ?? ''
        const boxNumber = item.display_box_number?.toLowerCase() ?? ''
        const location = item.city?.toLowerCase() ?? ''
        const customerNumber = item.customer_number ?? ''

        return (practiceName.includes(cleanSearch)
          || boxNumber.includes(cleanSearch)
          || location.includes(cleanSearch)
          || customerNumber.includes(cleanSearch))
          && (isActive ? filterByDropDown(item) : true)
          && (groupsSelected ? filterByGroups(item) : true)
      })
    }

    return filtered
  }

  useEffect(() => {
    if (customerNumber) {
      getData()
    }
  }, [customerNumber])

  const filteredItems = useMemo(() => {
    return bioBoxes
      ? getFilteredBioBoxes(search, statusFilter)
      : null
  }, [search, statusFilter, bioBoxes, dropDownFilter, selectedGroups])

  const handleExport = async () => {
    if (filteredItems) {
      setExporting(true)
      await exportBioBoxes(filteredItems)
      setExporting(false)
    }
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const searchValue = event.target.value
    setSearch(searchValue)
  }

  const debouncedHandleSearch = debounce(handleSearch, 50)

  const complianceStatuses = useMemo(() => {
    const initialStatus = { 1: 0, 2: 0, 3: 0, 4: 0 }
    if (bioBoxes) {
      bioBoxes.forEach((item) => {
        const key = (item.compliance[0].compliance_status ?? 4) as 1 | 2 | 3 | 4
        initialStatus[key]++
      })
    }
    return initialStatus
  }, [bioBoxes])

  const complianceMessage = (status: number): string => {
    const statusMessages: Record<number, string> = {
      1: 'Valid',
      2: 'Expiring Soon',
      3: 'Expired',
      4: 'Inactive',
    }

    return statusMessages[status] || 'Inactive'
  }

  const complianceSummaries = [
    ComplianceStatus.Expired,
    ComplianceStatus.Expiring,
    ComplianceStatus.Ok,
    ComplianceStatus.Inactive,
  ]

  const onUpdateBioBox = (box: FormattedBioBox) => {
    if (bioBoxes && box) {
      const updatedBioBoxes = bioBoxes.map(b => {
        if (b.box_id === box.box_id) b = box
        return b
      })
      setBioBoxes(updatedBioBoxes)
    }
    dispatch(addSuccess('Box has been updated'))
  }

  const handleChangeSelect = (field: string, value: string | Groups[]) => {
    if (field === 'status') {
      setDropDownFilter(value as string)
    } else {
      setSelectedGroups(value as Groups[])
    }
  }

  const updatePractice = async (practice: ChildPracticeMetadata, box: FormattedBioBox) => {
    try {
      const { customer_number: newCustomerNumber } = practice
      const { customer_number: oldCustomerNumber, box_id } = box

      if (newCustomerNumber === oldCustomerNumber) return

      const biobox = {
        customer_number: newCustomerNumber
      } as unknown as UpdateBioboxPracticePayload

      const {
        city,
        doctor_name,
        customer_number: updatedCustomerNumber,
        name,
        state
      } = await updateBoxPractice(box_id, biobox, customerNumber)

      const updatedBox = { ...box, city, doctor_name, customer_number: updatedCustomerNumber, name, state }

      const updatedBoxes = bioBoxes?.map(b => {
        return b.box_id === updatedBox.box_id ? updatedBox : b
      })
      setBioBoxes(updatedBoxes as FormattedBioBox[])

      dispatch(addSuccess('Box has been updated'))

    } catch (error) {
      console.error(error)
      dispatch(addError(error instanceof Error ? error.message : 'There was a problem updating the box'))
    }
  }

  return (
    <>
      <Card sx={{ width: "100%" }} variant="outlined">
        <Box className="box-header flex flex-space-between">
          <Box>Spore Test Boxes</Box>
          <Box className="flex">
            {
              complianceSummaries.map((status, index) => {
                return (
                  <Box key={`plip-container-${status}`}>
                    <CompliancePlip message={complianceMessage(status)} status={status} className="m-r-8" />
                    <span className={`${index < complianceSummaries.length - 1 ? 'm-r-12' : ''} muted-text`.trim()}>
                      {complianceStatuses[status]}
                    </span>
                  </Box>
                )
              })
            }
          </Box>
        </Box>
        <Box className="table-subheader flex flex-space-between">
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <TextField
              sx={{ minWidth: 200, marginRight: '10px' }}
              size="small"
              label="Search"
              onChange={debouncedHandleSearch}
              InputProps={
                {
                  endAdornment: (
                    <IconButton>
                      <FontAwesomeIcon icon="magnifying-glass" size="sm" className="m-l-8" />
                    </IconButton>
                  )
                }
              }
            />
            {statusFilter === 'active' && (
              <FormControl fullWidth size="small" sx={{ marginRight: '10px' }}>
                <Select
                  sx={{ minWidth: 130 }}
                  value={dropDownFilter}
                  onChange={(e) => handleChangeSelect('status', e.target.value)}
                  size="small"
                >
                  <MenuItem value="active">All Active</MenuItem>
                  <MenuItem value="1">Up to Date</MenuItem>
                  <MenuItem value="2">Attention Needed</MenuItem>
                  <MenuItem value="3">Expired</MenuItem>
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth size="small">
              <Autocomplete
                style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
                multiple
                disableCloseOnSelect
                options={groups}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                size="small"
                value={selectedGroups}
                onChange={(e, value) => handleChangeSelect('groups', value)}
                renderInput={(params) => <TextField {...params} label="Filter by Group" />}
              />
            </FormControl>
          </Box>
          <Box>
            <InventoryButtons statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
          </Box>
        </Box>
        <Box
          sx={{ paddingBottom: 0 }}
          className="table-subheader flex flex-end"
        >
          <Button
            sx={{
              "&.Mui-disabled": {
                color: "white",
              },
            }}
            variant="contained"
            onClick={handleExport}
            disabled={exporting}
          >
            {!exporting ? "Export" : "Exporting..."}
          </Button>
        </Box>
        <Box sx={{ padding: '16px' }}>
          <BiologicalInventoryTable
            bioBoxes={filteredItems}
            onUpdate={onUpdateBioBox}
            updateBoxPractice={updatePractice}
          />
        </Box>
      </Card>
    </>

  )
}

export default BiologicalInventory
